import React from 'react';
import { motion } from 'framer-motion';
import { FiAward, FiHeart, FiTruck, FiShield, FiSmile, FiStar } from 'react-icons/fi';
import { BsQuote } from 'react-icons/bs';
import Typewriter from '../components/common/Typewriter';

const About = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.2 } }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center bg-[#0a0a0a] bg-[url('https://images.unsplash.com/photo-1490481651871-ab68de25d43d?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80')] bg-cover bg-center">
        <div className="absolute inset-0 bg-black/75 backdrop-blur-[1px]" />
        <div className="container mx-auto px-4 relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl md:text-7xl font-display font-bold text-white mb-6 tracking-tight min-h-[1.2em]">
              Crafting <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-light to-secondary-light">
                <Typewriter text="Excellence" speed={150} delay={500} />
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 font-light max-w-2xl mx-auto leading-relaxed">
              More than just fashion—it's a statement of quality, passion, and identity.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Founder's Story */}
      <section className="py-24 bg-white dark:bg-[#050505]">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center gap-16">
            <motion.div
              className="w-full md:w-2/3 mx-auto text-center"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <BsQuote className="text-6xl text-primary-light/20 mb-6 mx-auto" />
              <h2 className="text-4xl font-display font-bold mb-8 text-gray-900 dark:text-white">
                The Story Behind <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-light to-secondary-light">TRENDZ</span>
              </h2>
              <div className="space-y-6 text-lg text-gray-600 dark:text-gray-300 leading-relaxed font-light">
                <p>
                  Hey, I'm <strong className="text-gray-900 dark:text-white">Huzaifa Kasi</strong>. TRENDZ wasn't just built to sell clothes; it was born from a vision to bridge the gap between premium international aesthetics and accessibility in Pakistan.
                </p>
                <p>
                  I started with a simple idea: creating a brand that feels personal. Every fabric chosen, every design approved, and every package shipped carries a piece of that dedication. It's been a journey of learning—mastering quality control, understanding customer needs, and building a platform that makes shopping a delight.
                </p>
                <p>
                  Today, TRENDZ represents more than a store. It's a promise of quality and a celebration of style. Thank you for being part of this story.
                </p>
                <div className="pt-8 border-t border-gray-100 dark:border-white/5 flex flex-col items-center">
                  <motion.div
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    transition={{ delay: 0.5, duration: 2 }}
                    className="flex flex-col items-center"
                  >
                    <span className="text-4xl font-signature text-primary-light italic" style={{ fontFamily: '"Great Vibes", cursive' }}>
                      KASI
                    </span>
                    <span className="text-xs uppercase tracking-[0.3em] text-gray-500 mt-2 font-bold">Official Signature</span>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values/Features Grid */}
      <section className="py-24 bg-gray-50 dark:bg-black/40">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Why Choose Us?</h2>
            <div className="w-24 h-1 bg-primary-light mx-auto rounded-full"></div>
          </div>

          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {[
              { icon: FiAward, title: "Premium Quality", desc: "Hand-picked fabrics and meticulous stitching for lasting elegance." },
              { icon: FiTruck, title: "Fast Delivery", desc: "Rapid, reliable shipping across Pakistan to your doorstep." },
              { icon: FiShield, title: "Secure Checkout", desc: "100% safe payments including Cash on Delivery options." },
              { icon: FiHeart, title: "Customer Love", desc: "Dedicated support team ensuring your satisfaction 24/7." }
            ].map((feature, idx) => (
              <motion.div
                key={idx}
                variants={itemVariants}
                whileHover={{ y: -10 }}
                className="bg-white dark:bg-dark-card p-8 rounded-2xl shadow-lg border border-gray-100 dark:border-white/5 group hover:border-primary-light/30 transition-all duration-300"
              >
                <div className="w-14 h-14 rounded-xl bg-primary-light/10 text-primary-light flex items-center justify-center text-3xl mb-6 group-hover:bg-primary-light group-hover:text-white transition-colors duration-300">
                  <feature.icon />
                </div>
                <h3 className="text-xl font-bold mb-3">{feature.title}</h3>
                <p className="text-gray-500 dark:text-gray-400 leading-relaxed">
                  {feature.desc}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Contact Banner */}
      <section className="py-20 bg-gradient-to-r from-primary-light to-secondary-light relative overflow-hidden">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="absolute -right-20 -top-20 w-96 h-96 bg-white/10 rounded-full blur-3xl"></div>
        <div className="container mx-auto px-4 relative z-10 text-center text-white">
          <h2 className="text-4xl font-bold mb-6">Have Questions?</h2>
          <p className="text-xl mb-8 opacity-90 max-w-xl mx-auto">
            We're here to help you find the perfect style. Reach out to us anytime.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-6">
            <a
              href="mailto:trendz.supp0rt@gmail.com"
              className="inline-flex items-center justify-center gap-2 bg-white text-primary-light px-8 py-3 rounded-full font-bold hover:bg-gray-100 transition-colors shadow-lg"
            >
              <FiSmile className="text-xl" />
              trendz.supp0rt@gmail.com
            </a>
            <a
              href="tel:+923708426610"
              className="inline-flex items-center justify-center gap-2 bg-black/20 backdrop-blur-sm text-white border-2 border-white/30 px-8 py-3 rounded-full font-bold hover:bg-black/30 transition-all"
            >
              <FiStar className="text-xl" />
              +92 3708426610
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
